import { useState, useCallback } from 'react';
import {
  AppStorage,
  ChildProfile,
  WordEntry,
  WordProgress,
  DEFAULT_STORAGE,
  POINTS_CONFIG,
} from '@/lib/index';
import { WORD_BANK } from '@/data/wordbank';

const STORAGE_KEY = 'yuyu_app_v3';
const DEFAULT_CHILD: ChildProfile = {
  id: 'default',
  name: '宥宥',
  age: 6,
  avatar: '🐼',
};

function loadStorageFromLocal(): AppStorage {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (_error) {
    return DEFAULT_STORAGE;
  }
  return DEFAULT_STORAGE;
}

function saveStorageToLocal(data: AppStorage): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (_error) {
    return;
  }
}

function calcNextDueDate(mastery: 0 | 1 | 2): string {
  const today = new Date();
  const daysToAdd = mastery === 2 ? 7 : mastery === 1 ? 3 : 1;
  today.setDate(today.getDate() + daysToAdd);
  return today.toISOString().split('T')[0];
}

interface UseStorageReturn {
  storage: AppStorage;
  setStorage: (updater: (prev: AppStorage) => AppStorage) => void;
  getTodayWords: () => WordEntry[];
  getWordsForReview: () => WordEntry[];
  updateWordProgress: (wordId: string, lang: 'zh' | 'jp' | 'en', level: 0 | 1 | 2) => void;
  unlockTodaySession: () => void;
  addPoints: (delta: number, reason: string) => void;
  getChildPoints: () => number;
  getStats: () => {
    totalLearned: number;
    masteryZh: number;
    masteryJp: number;
    masteryEn: number;
    streak: number;
  };
  setDailyWordCount: (n: number) => void;
  setDailyPhonicsCount: (n: number) => void;
}

export function useStorage(): UseStorageReturn {
  const [storage, setStorageState] = useState<AppStorage>(() => loadStorageFromLocal());

  const setStorage = useCallback((updater: (prev: AppStorage) => AppStorage) => {
    setStorageState((prev) => {
      const updated = updater(prev);
      saveStorageToLocal(updated);
      return updated;
    });
  }, []);

  const getTodayWords = useCallback((): WordEntry[] => {
    if (!storage.todayUnlocked || !storage.todayWordIds || storage.todayWordIds.length === 0) {
      return [];
    }

    const allWords = [...WORD_BANK, ...storage.customWords];
    return storage.todayWordIds
      .map((id) => allWords.find((w) => w.id === id))
      .filter((w): w is WordEntry => w !== undefined);
  }, [storage]);

  const getWordsForReview = useCallback((): WordEntry[] => {
    const today = new Date().toISOString().split('T')[0];
    const allWords = [...WORD_BANK, ...storage.customWords];

    return allWords.filter((word) => {
      const progress = storage.wordProgress[word.id];
      if (!progress) return false;
      return progress.nextDueDate <= today;
    });
  }, [storage]);

  const updateWordProgress = useCallback(
    (wordId: string, lang: 'zh' | 'jp' | 'en', level: 0 | 1 | 2) => {
      setStorage((prev) => {
        const existing = prev.wordProgress[wordId] || {
          wordId,
          masteryZh: 0 as const,
          masteryJp: 0 as const,
          masteryEn: 0 as const,
          nextDueDate: new Date().toISOString().split('T')[0],
          reviewCount: 0,
        };

        const updated: WordProgress = {
          ...existing,
          reviewCount: existing.reviewCount + 1,
        };

        if (lang === 'zh') updated.masteryZh = level;
        if (lang === 'jp') updated.masteryJp = level;
        if (lang === 'en') updated.masteryEn = level;

        const maxMastery = Math.max(updated.masteryZh, updated.masteryJp, updated.masteryEn) as
          | 0
          | 1
          | 2;
        updated.nextDueDate = calcNextDueDate(maxMastery);

        return {
          ...prev,
          wordProgress: {
            ...prev.wordProgress,
            [wordId]: updated,
          },
        };
      });
    },
    [setStorage]
  );

  const unlockTodaySession = useCallback(() => {
    setStorage((prev) => {
      const today = new Date().toISOString().split('T')[0];
      const allWords = [...WORD_BANK, ...prev.customWords];
      const normalWords = allWords.filter((w) => !w.isChar);
      const learned = Object.keys(prev.wordProgress);
      const unlearned = normalWords.filter((w) => !learned.includes(w.id));

      const count = prev.dailyWordCount || 3;
      const selectedWords = unlearned.slice(0, count);
      const todayWordIds = selectedWords.map((w) => w.id);

      const isNewDay = prev.lastStudyDate !== today;
      const newStreak = isNewDay ? prev.streak + 1 : prev.streak;

      return {
        ...prev,
        todayUnlocked: true,
        todayWordIds,
        lastStudyDate: today,
        streak: newStreak,
      };
    });
  }, [setStorage]);

  const addPoints = useCallback(
    (delta: number, reason: string) => {
      setStorage((prev) => {
        const newPoints = Math.max(0, prev.points + delta);

        const logKey = 'yuyu_points_log';
        try {
          const logStr = localStorage.getItem(logKey);
          const log = logStr ? JSON.parse(logStr) : [];
          log.push({
            date: new Date().toISOString(),
            delta,
            reason,
            total: newPoints,
          });
          localStorage.setItem(logKey, JSON.stringify(log));
        } catch (_error) {
          return { ...prev, points: newPoints };
        }

        return {
          ...prev,
          points: newPoints,
        };
      });
    },
    [setStorage]
  );

  const getChildPoints = useCallback((): number => {
    return storage.points || 0;
  }, [storage]);

  const getStats = useCallback(() => {
    const progressEntries = Object.values(storage.wordProgress);
    const totalLearned = progressEntries.length;

    const masteryZh =
      totalLearned > 0
        ? Math.round(
            (progressEntries.filter((p) => p.masteryZh === 2).length / totalLearned) * 100
          )
        : 0;
    const masteryJp =
      totalLearned > 0
        ? Math.round(
            (progressEntries.filter((p) => p.masteryJp === 2).length / totalLearned) * 100
          )
        : 0;
    const masteryEn =
      totalLearned > 0
        ? Math.round(
            (progressEntries.filter((p) => p.masteryEn === 2).length / totalLearned) * 100
          )
        : 0;

    return {
      totalLearned,
      masteryZh,
      masteryJp,
      masteryEn,
      streak: storage.streak || 0,
    };
  }, [storage]);

  const setDailyWordCount = useCallback(
    (n: number) => {
      setStorage((prev) => ({
        ...prev,
        dailyWordCount: n,
      }));
    },
    [setStorage]
  );

  const setDailyPhonicsCount = useCallback(
    (n: number) => {
      setStorage((prev) => ({
        ...prev,
        dailyPhonicsCount: n,
      }));
    },
    [setStorage]
  );

  return {
    storage,
    setStorage,
    getTodayWords,
    getWordsForReview,
    updateWordProgress,
    unlockTodaySession,
    addPoints,
    getChildPoints,
    getStats,
    setDailyWordCount,
    setDailyPhonicsCount,
  };
}
